import { Box, SvgIcon, Typography } from '@mui/material';
import { chunk, isEmpty, isPlainObject, memoize } from 'lodash-es';
import React, { FC, Fragment, ReactNode } from 'react';

import InfoIcon from '@sbu/assets.svg/src/info.svg';
import { Motion } from '@sbu/components.motion';
import { Divider, Tooltip, useTheme } from '@sumologic/yama-ds-components';

import { checkEmpty } from 'core/services/utils';
import { RecordType } from 'shared/components/internal/Records';
import { type EntityTypeFieldsAndIndicator } from 'shared/components/internal/Records/useEntityTypeRecordFields';

import { Address, AddressType } from './Address';
import { IngestSource } from './IngestSource';
import { Mapping } from './Mapping';
import { useMappingFields } from './Mapping/useMappingFields';
import { RecordProperty } from './RecordProperty';
import { useExtractedFields } from './useExtractedFields';
import { useRemainingFields } from './useRemainingFields';

export const METADATA_PREFIX = 'metadata_';
export const SRC_PREFIX = 'srcDevice_';
export const DST_PREFIX = 'dstDevice_';
export const DEVICE_PREFIX = 'device_';

const extractedFieldsHint =
  'Extracted Fields shows the structure of the Record data before it was mapped into the Cloud SIEM schema.';

export const hasValue = (value: ReactNode) =>
  value !== 'Unassigned' && !checkEmpty(value);

const IGNORED_FIELDS = [
  'year',
  'month',
  'day',
  'hour',
  'objectType',
  'friendlyName',
  '_type',
  '_id',
  'timestamp',
  'uid',
];

const parseRecord = memoize((origRecord: RecordType) => {
  const remaining = { ...origRecord };
  const metadata: RecordType = {};
  const sourceDevice: RecordType = {};
  const destinationDevice: RecordType = {};
  const device: RecordType = {};
  let fields: RecordType = {};

  Object.entries(remaining).forEach(([key, value]) => {
    if (IGNORED_FIELDS.includes(key)) {
      delete remaining[key];
    }

    // Parse metadata
    else if (key.startsWith(METADATA_PREFIX)) {
      metadata[key] = value;
      delete remaining[key];
    }

    // Parse source
    else if (key.startsWith(SRC_PREFIX)) {
      sourceDevice[key.slice(SRC_PREFIX.length)] = value;
      delete remaining[key];
    }

    // Parse destination
    else if (key.startsWith(DST_PREFIX)) {
      destinationDevice[key.slice(DST_PREFIX.length)] = value;
      delete remaining[key];
    }

    // Parse device
    else if (key.startsWith(DEVICE_PREFIX)) {
      device[key.slice(DEVICE_PREFIX.length)] = value;
      delete remaining[key];
    }

    // Parse fields
    else if (key === 'fields' && isPlainObject(remaining[key])) {
      fields = remaining[key];
      delete remaining[key];
    }
  });

  return {
    destinationDevice,
    fields,
    metadata,
    remaining,
    sourceDevice,
    device,
  };
});

interface RecordOverviewProps {
  record: RecordType;
  showEmpty: boolean;
  entityTypeFieldsAndIndicator?: EntityTypeFieldsAndIndicator;
}

const useDevices = (
  sourceDevice: RecordType,
  destinationDevice: RecordType,
  device: RecordType
) => {
  const devices: { type: AddressType; props: RecordType }[] = [];
  if (!isEmpty(sourceDevice)) {
    devices.push({ type: 'source', props: sourceDevice });
  }
  if (!isEmpty(destinationDevice)) {
    devices.push({ type: 'destination', props: destinationDevice });
  }
  if (!isEmpty(device) && !devices.map((d) => d.props.ip).includes(device.ip)) {
    devices.push({ type: 'device', props: device });
  }
  return devices;
};

const useFieldsForDetailsSection = (
  record: RecordType,
  remaining: RecordType,
  metadata: RecordType,
  showEmpty: boolean
) => {
  const isFailedRecord = record.objectType === 'FailedRecord';
  const remainingFields = useRemainingFields(remaining, showEmpty);
  const mappingFields = useMappingFields(metadata, showEmpty);

  return [...remainingFields, ...(isFailedRecord ? mappingFields : [])];
};

export const RecordOverview: FC<
  React.PropsWithChildren<RecordOverviewProps>
> = ({ record, showEmpty, entityTypeFieldsAndIndicator }) => {
  const {
    fields,
    metadata,
    remaining,
    sourceDevice,
    destinationDevice,
    device,
  } = parseRecord(record);
  const devices = useDevices(sourceDevice, destinationDevice, device);
  const extractedFields = useExtractedFields(fields, showEmpty);
  const detailFields = useFieldsForDetailsSection(
    record,
    remaining,
    metadata,
    showEmpty
  );
  const { palette } = useTheme();

  const displayDetailsSection = !isEmpty(detailFields);
  const displayMetadataSections =
    record.objectType !== 'FailedRecord' && !isEmpty(metadata);
  const displayExtractedFieldsSection = !isEmpty(extractedFields);

  /* TODO: recreate grid/flexbox and add dl/dt/dd tags instead of div */
  return (
    <Motion>
      {chunk(devices, 2).map((devicesRow, index) => (
        <Fragment key={index}>
          <Box display='flex' marginBottom={2.5}>
            {devicesRow.map((device, rowIndex) => (
              <Fragment key={device.type}>
                {rowIndex > 0 && (
                  <Box
                    component={Motion}
                    marginX={2.5}
                    position='relative'
                    type='item'
                  >
                    <Divider orientation='vertical' />
                  </Box>
                )}
                <Box
                  component={Motion}
                  flex={1}
                  position='relative'
                  type='item'
                >
                  <Address
                    allRecordData={record}
                    data={device.props}
                    showEmpty={showEmpty}
                    type={device.type}
                    entityTypeFieldsAndIndicator={entityTypeFieldsAndIndicator}
                  />
                </Box>
              </Fragment>
            ))}
          </Box>
          <Divider sx={{ my: 2.5 }} />
        </Fragment>
      ))}
      {displayDetailsSection && (
        <Motion type='item'>
          <Typography variant='h2' mb={1}>
            Details
          </Typography>
          <Box sx={{ columnWidth: '300px' }}>
            {detailFields.map(({ name, value, label }) => (
              <Box
                key={`record-prop-${name}`}
                sx={{ breakInside: 'avoid-column' }}
              >
                <RecordProperty
                  entityTypeFieldsAndIndicator={entityTypeFieldsAndIndicator}
                  label={label}
                  name={name}
                  record={record}
                >
                  {value}
                </RecordProperty>
              </Box>
            ))}
          </Box>
        </Motion>
      )}
      {displayMetadataSections && (
        <Motion type='item'>
          <Divider sx={{ my: 2.5 }} />
          <Box display='flex' marginBottom={2.5}>
            <Box component={Motion} flex={1} position='relative' type='item'>
              <IngestSource
                allRecordData={record}
                data={metadata}
                showEmpty={showEmpty}
                entityTypeFieldsAndIndicator={entityTypeFieldsAndIndicator}
              />
            </Box>
            <Box
              component={Motion}
              marginX={2.5}
              position='relative'
              type='item'
            >
              <Divider orientation='vertical' />
            </Box>
            <Box component={Motion} flex={1} position='relative' type='item'>
              <Mapping
                allRecordData={record}
                data={metadata}
                showEmpty={showEmpty}
                entityTypeFieldsAndIndicator={entityTypeFieldsAndIndicator}
              />
            </Box>
          </Box>
        </Motion>
      )}
      {displayExtractedFieldsSection && (
        <Motion type='item'>
          <Divider sx={{ my: 2.5 }} />
          <Box>
            <Typography variant='h2' mb={1}>
              Extracted Fields
              <Tooltip title={extractedFieldsHint}>
                <span>
                  <SvgIcon
                    component={InfoIcon}
                    inheritViewBox
                    sx={{
                      bottom: '2px',
                      color: palette.tokens.YamaTypographyColorIcon,
                      height: '12px',
                      cursor: 'help',
                      position: 'relative',
                      marginLeft: 1.25,
                      width: '12px',
                    }}
                  />
                </span>
              </Tooltip>
            </Typography>
            <Box sx={{ columnWidth: '300px' }}>
              {extractedFields.map(({ name, label, value }) => (
                <Box
                  key={`record-prop-${label}`}
                  sx={{ breakInside: 'avoid-column' }}
                >
                  <RecordProperty
                    isExtractedField={true}
                    label={label}
                    name={name}
                    record={record}
                    entityTypeFieldsAndIndicator={entityTypeFieldsAndIndicator}
                  >
                    {value}
                  </RecordProperty>
                </Box>
              ))}
            </Box>
          </Box>
        </Motion>
      )}
    </Motion>
  );
};
